import React, { useState, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Navbar from '../commonComponents/Navbar';
import Footer from '../commonComponents/Footer';
import { Box, Container, Grid, Typography, TextField, Button, Stack, CircularProgress } from '@mui/material';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { useDispatch, useSelector } from 'react-redux';
import { submitContactForm, resetContact } from '../../store/slices/contactSlice';
import ServicesHero from '../commonComponents/ServicesHero';
import contactHeroImg from '../../assets/images/contact_banner.png'

import emailIcon from '../../assets/images/ic_baseline-email.png'
import { toast } from 'react-toastify';

const supportMail = process.env.REACT_APP_SUPPORT_MAIL || 'support@payible.com';

const Contact = () => {
  const dispatch = useDispatch();
  const { status, message: responseMessage } = useSelector((state) => state.contact);
  const [leftRef, leftVisible] = useScrollAnimation(0.15);
  const [rightRef, rightVisible] = useScrollAnimation(0.1);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  const isLoading = status === 'loading';
  const isSuccess = status === 'success';

  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    dispatch(resetContact());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handlePhoneKeyDown = (e) => {
    if (/^[a-zA-Z]$/.test(e.key)) e.preventDefault();
  };

  const validate = () => {
    const e = {};
    if (!formState.name.trim()) e.name = 'Name is required';
    if (!formState.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formState.email)) e.email = 'Invalid email address';
    if (!formState.phone.trim()) e.phone = 'Phone number is required';
    if (!formState.message.trim()) e.message = 'Message is required';
    return e;
  };

  useEffect(() => {
    if (status === 'success') {
      toast.success('Message sent successfully!');
      setFormState({ name: '', email: '', phone: '', message: '' });
      setCaptchaToken(null);
      captchaRef.current?.reset();
      dispatch(resetContact());
    }
    if (status === 'error') {
      toast.error(responseMessage || 'Failed to send message');
      setCaptchaToken(null);
      captchaRef.current?.reset();
      dispatch(resetContact());
    }
  }, [status]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the captcha');
      return;
    }
    if (isLoading) return;
    dispatch(submitContactForm({ ...formState, captchaToken }));
  };

  const handleCaptchaChange = (token) => setCaptchaToken(token);
  const handleCaptchaExpire = () => setCaptchaToken(null);

  return (
    <div style={{ backgroundColor: 'var(--color-bg)', color: '#fff', minHeight: '100vh' }} className="page-enter">
      <Navbar />

      <ServicesHero
        heading="Access the Infrastructure"
        highlightedText=""
        description="Whether you are executing transactions or wanting to diversify your portfolio, our team is available to support your requirements."
        subDescription=""
        image={contactHeroImg}
      />

      {/* Contact Content Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 10, md: 14 },
          backgroundColor: 'var(--color-bg)',
          color: '#fff',
        }}
      >
        <Container>
          <Grid container spacing={{ xs: 6, md: 8, lg: 12 }} sx={{ justifyContent: 'center' }}>
            {/* Left side: Contact Info Cards */}
            <Grid
              size={{ xs: 12, md: 5 }}
              ref={leftRef}
              className={`anim-fade-left${leftVisible ? ' in-view' : ''}`}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                  height: '100%',
                  width: '100%',
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                <Typography
                  variant="h2"
                  className="faq-title"
                  sx={{
                    fontSize: {
                      xs: '2rem',
                      sm: '2.5rem',
                      md: '2.9rem',
                    },
                    mb: 3,
                    letterSpacing: '-0.02em',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Contact Us 24/7
                </Typography>

                <Typography
                  className="faq-desc"
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    maxWidth: '100%',
                    textAlign: { xs: 'center', md: 'left' },
                    mb: 5,
                  }}
                >
                  Get in touch with Payible for onboarding inquiries, OTC trading discussions, or general operational and compliance-related questions.
                </Typography>

                {/* Contact Info Cards */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={emailIcon}
                        alt="Logo"
                        sx={{ width: 40, height: 40 }}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography
                        component="a"
                        href={`mailto:${supportMail}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: '1.05rem',
                          color: '#fff',
                          fontWeight: 600,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        support@payible.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right side: Contact Form */}
            <Grid
              size={{ xs: 12, md: 7 }}
              ref={rightRef}
              className={`anim-fade-right${rightVisible ? ' in-view' : ''}`}
            >
              <Box sx={{ position: 'relative' }}>
                {/* Purple glow */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -70,
                    right: -70,
                    width: '300px',
                    height: '300px',
                    borderRadius: '60%',
                    background: 'radial-gradient(circle, rgba(120, 40, 255, 0.55) 0%, rgba(80, 0, 200, 0) 70%)',
                    filter: 'blur(100px)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />

                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: '100%',
                    mx: 'auto',
                    backgroundColor: '#1C1C1F',
                    borderRadius: '24px',
                    p: { xs: 5, sm: 6, md: 7 },
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Top-Right Decorative Graphic */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '120px',
                      height: '120px',
                      overflow: 'hidden',
                      borderTopRightRadius: '24px',
                      pointerEvents: 'none',
                    }}
                  >
                    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="120" cy="0" r="45" stroke="currentColor" strokeWidth="6" style={{ color: 'var(--color-primary)' }} />
                      <circle cx="120" cy="0" r="75" stroke="currentColor" strokeWidth="6" style={{ color: 'var(--color-primary)' }} />
                    </svg>
                  </Box>

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={4.5}>
                      <TextField
                        fullWidth variant="standard" name="name" label="First Name"
                        value={formState.name} onChange={handleChange}
                        error={!!errors.name} helperText={errors.name}
                        disabled={isLoading || isSuccess} sx={inputStyles}
                      />
                      <TextField
                        fullWidth variant="standard" name="email" type="text" label="Email Address"
                        value={formState.email} onChange={handleChange}
                        error={!!errors.email} helperText={errors.email}
                        disabled={isLoading || isSuccess} sx={inputStyles}
                      />
                      <TextField
                        fullWidth variant="standard" name="phone" label="Phone Number"
                        value={formState.phone} onChange={handleChange} onKeyDown={handlePhoneKeyDown}
                        error={!!errors.phone} helperText={errors.phone}
                        disabled={isLoading || isSuccess} sx={inputStyles}
                      />
                      <TextField
                        fullWidth variant="standard" name="message" label="Message"
                        multiline rows={2} value={formState.message} onChange={handleChange}
                        error={!!errors.message} helperText={errors.message}
                        disabled={isLoading || isSuccess} sx={inputStyles}
                      />

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 1, flexWrap: 'wrap' }}>
                        <ReCAPTCHA
                          ref={captchaRef}
                          sitekey={siteKey}
                          theme="dark"
                          onChange={handleCaptchaChange}
                          onExpired={handleCaptchaExpire}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isLoading || !captchaToken}
                          sx={{
                            textTransform: 'none',
                            py: 1, px: 5,
                            fontWeight: 700,
                            fontSize: '1rem',
                            borderRadius: '8px',
                            backgroundColor: 'var(--color-primary)',
                            color: '#22183A',
                            boxShadow: '0 0 20px rgba(122, 212, 197, 0.35)',
                            transition: 'all 0.3s ease',
                            minWidth: 160,
                            '&:hover': {
                              backgroundColor: '#8CE2D4',
                              boxShadow: '0 0 25px rgba(122, 212, 197, 0.55)',
                              transform: 'translateY(-2px)',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(47,204,178,0.3)',
                              color: '#22183A',
                            },
                          }}
                        >
                          {isLoading
                            ? <CircularProgress size={20} sx={{ color: '#22183A' }} />
                            : 'Get in Touch'
                          }
                        </Button>
                      </Box>
                    </Stack>
                  </form>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </div>
  );
};

// Common CSS styling for the dark themed Underline Inputs
const inputStyles = {
  '& .MuiInput-root': {
    color: '#fff',
    fontFamily: 'var(--font-secondary)',
    paddingBottom: '8px',
    paddingTop: '8px',
    fontSize: '1.05rem',
    '&:before': {
      borderBottomColor: 'rgba(255, 255, 255, 0.2)',
      borderBottomWidth: '1.5px',
    },
    '&:hover:not(.Mui-disabled):before': {
      borderBottomColor: 'rgba(255, 255, 255, 0.45)',
      borderBottomWidth: '1.5px',
    },
    '&:after': {
      borderBottomColor: 'var(--color-primary)',
      borderBottomWidth: '2px',
    },
    '&.Mui-error:before': {
      borderBottomColor: '#f44336',
      borderBottomWidth: '1.5px',
    },
    '&.Mui-error:after': {
      borderBottomColor: '#f44336',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #1C1C1F inset',
      WebkitTextFillColor: '#ffffff',
      caretColor: '#ffffff',
      transition: 'background-color 5000s ease-in-out 0s',
    },
    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: '0 0 0 1000px #1C1C1F inset',
      WebkitTextFillColor: '#ffffff',
    },
    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px #1C1C1F inset',
      WebkitTextFillColor: '#ffffff',
    },
    '& input:-webkit-autofill:active': {
      WebkitBoxShadow: '0 0 0 1000px #1C1C1F inset',
      WebkitTextFillColor: '#ffffff',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.55)',
    fontFamily: 'var(--font-secondary)',
    fontSize: '1.05rem',
    '&.Mui-focused': {
      color: 'var(--color-primary)',
    },
    '&.Mui-error': {
      color: '#f44336',
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#f44336',
    fontFamily: 'var(--font-secondary)',
    fontSize: '0.8rem',
    marginTop: '6px',
  },
};

export default Contact;
